import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { lionSelectors, LionTranslate } from '@rosetta/react-lion';
import { authSelectors, LOGIN_STATUS } from '@rosetta/react-auth';
import { appActions, appSelectors } from '../reducer/app-reducer';
import { getAvailableInterfaceLanguages, getSupportLocale } from '../services/language-helpers';
import { RSDropdown } from '@rosetta/rs-dropdown';
import { menuStyle } from '../../../styles/FooterLocaleDropdown';
import styled from '@emotion/styled';
import { withWindowDimensions } from '@rosetta/react-common-ui';
import { footerTextColor } from '../../../styles/colors';
import { isMobile as isMobileDetected } from 'react-device-detect';
import { parse } from 'query-string';
import Cookies from 'universal-cookie';
import moment from 'moment';

const cookies = new Cookies();
let isRtl = false;

// The smallest breakpoints mocked in Figma where UI changes happen
const smallScreen = '@media only screen and (max-width: 480px)';
const extraSmallScreen = '@media only screen and (max-width: 320px)';

const AppFooterDiv = styled.div((props) => {
  let style = {
    backgroundColor: 'rgba(17, 24, 31, 1)',
    bottom: '0',
    left: '0',
    right: '0',
    fontSize: '14px',
    zIndex: '2',
    background: 'rgba(17, 24, 31, 1)',

    position: isMobileDetected ? 'relative' : 'fixed',
    display: 'flex',
    '& > div': {
      padding: '20px',
      display: 'flex',
      width: '100%',
      maxWidth: '1200px',
      justifyContent: 'space-between',
      margin: '0 auto',
      [smallScreen]: {
        padding: '10px'
      },
      [extraSmallScreen]: {
        flexDirection: 'column'
      }
    }
  };
  return style;
});

const LegalLinks = styled.div((props) => {
  let style = {
    display: 'flex',
    [smallScreen]: {
      flexDirection: 'column',
      width: 'auto'
    },
    [extraSmallScreen]: {
      alignItems: 'center'
    }
  };
  return style;
});

const FooterLinkSpan = styled.span((props) => {
  let style = {
    display: 'flex',
    alignItems: 'center',
    marginRight: '20px',
    '&:last-of-type': {
      marginRight: 0
    },
    [smallScreen]: {
      margin: '10px 0',
      '&:first-of-type': {
        marginTop: 0
      }
    }
  };
  return style;
});

const FooterLink = styled.a((props) => {
  let style = {
    textDecoration: 'none',
    cursor: 'pointer',
    outline: '0',
    fontWeight: '400',
    lineHeight: '22.4px',
    color: footerTextColor,
    backgroundColor: 'transparent',
    ':hover': {
      color: footerTextColor,
      backgroundColor: 'transparent',
      textDecoration: 'underline'
    }
  };
  return style;
});

const LocaleSelectorContainer = styled.div((props) => {
  let style = {
    minWidth: '250px',
    [smallScreen]: {
      minWidth: '160px'
    },
    [extraSmallScreen]: {
      alignSelf: 'center',
      order: -1,
      minWidth: '250px',
      margin: '15px 0'
    }
  };
  return style;
});

const defaultCookieOptions = () => {
  const extractHost = () => {
    const host = window.location.host.split(':')[0];
    let subdomainArray = host.split('.');
    if (subdomainArray.length > 1) {
      subdomainArray.shift();
    }
    return subdomainArray.join('.');
  };

  const cookieDomain = extractHost();
  let cookieOptions = {};
  // this is so development on localhost (which doesn't need a domain) works
  if (cookieDomain !== 'localhost') {
    cookieOptions.domain = cookieDomain;
  }
  cookieOptions.expires = new Date(
    moment()
      .add(1, 'y')
      .format()
  );
  return cookieOptions;
};

//*********************************************
export class NewAppFooter extends PureComponent {
  copyrightStr = null;
  getCopyright = () => {
    if (!this.copyrightStr) {
      const baseString = this.props.lionTranslateString('email_footer_text1_copyright');
      const today = new Date();
      const year = today.getFullYear().toString();
      this.copyrightStr = baseString.replace('{{year}}', year);
    }
    return this.copyrightStr;
  };

  handleInterfaceLanguageSelected = (languageObj) => {
    this.props.setInterfaceLanguage(languageObj.value); // Save to the RSID cookie.
    const cookieOptions = defaultCookieOptions();
    cookies.set('locale', languageObj.value, cookieOptions); // Save to the locale cookie.
  };

  componentDidMount() {
    // if we have ?ui_language in the url, change ui language and update cookie for future visits
    // ps: maybe we could rename this param to `?locale=` ?
    const queryParamLanguage = parse(window.location.search)['ui_language'] || null;
    if (queryParamLanguage) {
      let uiLanguage = getAvailableInterfaceLanguages().find((o) => o.locale === queryParamLanguage) || null;
      if (uiLanguage) {
        this.props.setInterfaceLanguage(uiLanguage.value);
        const cookieOptions = defaultCookieOptions();
        cookies.set('locale', uiLanguage.value, cookieOptions); // Save to the locale cookie.
      }
    }
  }

  render = () => {
    if (!this.props.lionLoaded) {
      return null;
    }
    const supportLocale = getSupportLocale(this.props.currentLocale);

    isRtl = this.props.isInterfaceRtl;
    const isMobile = isMobileDetected;

    let links = null;
    if (this.props.userIsLoggedIn) {
      links = (
        <LegalLinks isMobile={isMobile} dataQa="footerLinks">
          <FooterLinkSpan isMobile={isMobile}>
            <FooterLink href="http://www.rosettastone.com/termsonline" target="_blank" tabIndex={-1}>
              <LionTranslate lionkey="_terms_of_service" />
            </FooterLink>
          </FooterLinkSpan>
          <FooterLinkSpan isMobile={isMobile}>
            <FooterLink
              href="http://www.rosettastone.com/interactiveproductprivacy"
              target="_blank"
              tabIndex={-1}
              isMobile={isMobile}
            >
              <LionTranslate lionkey="_privacy_policy" />
            </FooterLink>
          </FooterLinkSpan>
          <FooterLinkSpan isMobile={isMobile}>
            <FooterLink href="http://www.rosettastone.com/agreements" target="_blank" tabIndex={-1} isMobile={isMobile}>
              <LionTranslate lionkey="launchpad_footer_agreements" />
            </FooterLink>
          </FooterLinkSpan>
          <FooterLinkSpan isMobile={isMobile}>
            <FooterLink
              href="http://support.rosettastone.com/SupportINSProductTemplate"
              target="_blank"
              tabIndex={-1}
              isMobile={isMobile}
            >
              <LionTranslate lionkey="launchpad_footer_support" />
            </FooterLink>
          </FooterLinkSpan>
        </LegalLinks>
      );
    } else {
      links = (
        <LegalLinks isMobile={isMobile} data-qa="footerLinks">
          <FooterLinkSpan isMobile={isMobile}>
            <FooterLink
              href="https://resources.rosettastone.com/CDN/global/agreements/Online%20Interactive%20Product%20Privacy%20Policy_03192019.pdf"
              target="_blank"
              tabIndex={-1}
            >
              <LionTranslate lionkey="_privacy_policy" />
            </FooterLink>
          </FooterLinkSpan>
          <FooterLinkSpan isMobile={isMobile}>
            <FooterLink
              href="https://resources.rosettastone.com/CDN/us/agreements/Online%20Terms%20of%20Use%20%28combined%29_%2003202019.pdf"
              target="_blank"
              tabIndex={-1}
            >
              <LionTranslate lionkey="_terms_of_service" />
            </FooterLink>
          </FooterLinkSpan>
          <FooterLinkSpan isMobile={isMobile}>
            <FooterLink
              href={'https://support.rosettastone.com/s/?language=' + supportLocale}
              target="_blank"
              tabIndex={-1}
              isMobile={isMobile}
            >
              <LionTranslate lionkey="help_and_support_link" />
            </FooterLink>
          </FooterLinkSpan>
        </LegalLinks>
      );
    }

    return (
      <AppFooterDiv isMobile={isMobile}>
        <div>
          {links}
          <LocaleSelectorContainer isMobile={isMobile}>
            <RSDropdown
              dataQa="InterfaceLanguageMenu"
              buttonText=""
              itemsList={getAvailableInterfaceLanguages()}
              itemDisplayProperty="nativeName"
              itemKeyProperty="value"
              onSelectionHandler={this.handleInterfaceLanguageSelected}
              selectedKey={this.props.currentLocale}
              isRtl={isRtl}
              menuStyle={menuStyle}
            />
          </LocaleSelectorContainer>
        </div>
      </AppFooterDiv>
    );
  };
}

NewAppFooter.propTypes = {};

const mapStateToProps = (state, ownprops) => ({
  currentLocale: lionSelectors.getLionLanguage(state),
  isInterfaceRtl: appSelectors.isInterfaceRtl(state),
  lionLoaded: lionSelectors.getLionAppResourcesLoaded(state),
  lionTranslateString: (key, placeholders, values) => {
    return lionSelectors.getLionTranslatedString(state, key, placeholders, values);
  },
  userIsLoggedIn: authSelectors.loginStatus(state) === LOGIN_STATUS.LOGGED_IN
});

const wrappedActions = { setInterfaceLanguage: appActions.setInterfaceLanguage };
export const NewAppFooterContainer = connect(
  mapStateToProps,
  wrappedActions
)(withWindowDimensions(NewAppFooter));



// WEBPACK FOOTER //
// ./src/modules/app/components/NewAppFooter.jsx